import React, {
  createContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from 'react';

interface TimerContextType {
  minutes: string;
  seconds: string;
  startTimer: (duration: number) => void;
  resetTimer: () => void;
}

export const TimerContext = createContext<TimerContextType | undefined>(
  undefined
);

interface TimerProviderProps {
  children: ReactNode;
}

export const TimerProvider: React.FC<TimerProviderProps> = ({ children }) => {
  const [minutes, setMinutes] = useState<string>('00');
  const [seconds, setSeconds] = useState<string>('00');
  const [duration, setDuration] = useState<number>(0);
  const startTimeRef = useRef<number | null>(null);

  const startTimer = (timerDuration: number) => {
    setDuration(timerDuration);
    startTimeRef.current = Date.now();
  };

  const resetTimer = () => {
    setDuration(0);
    startTimeRef.current = null;
    setMinutes('00');
    setSeconds('00');
  };

  useEffect(() => {
    if (duration === 0 || !startTimeRef.current) return;

    const interval = setInterval(() => {
      const elapsedSeconds = Math.floor(
        (Date.now() - startTimeRef.current!) / 1000
      );
      const timeLeft = Math.max(duration - elapsedSeconds, 0);

      const mins = Math.floor(timeLeft / 60);
      const secs = timeLeft % 60;

      setMinutes(mins < 10 ? `0${mins}` : `${mins}`);
      setSeconds(secs < 10 ? `0${secs}` : `${secs}`);

      if (timeLeft === 0) {
        clearInterval(interval);
        resetTimer();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [duration]);

  return (
    <TimerContext.Provider value={{ minutes, seconds, startTimer, resetTimer }}>
      {children}
    </TimerContext.Provider>
  );
};
